import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export function ChangePasswordDialog({ forced = false, onChanged }) {
  const [open, setOpen] = useState(forced);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.current_password) er.current_password = "Podaj aktualne hasło.";
    if (form.new_password.length < 10 || !/[A-Za-z]/.test(form.new_password) || !/\d/.test(form.new_password))
      er.new_password = "Min. 10 znaków, litery i cyfry.";
    if (form.confirm !== form.new_password) er.confirm = "Hasła nie są identyczne.";
    setErrors(er);
    if (Object.keys(er).length) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Hasło zostało zmienione.");
      setForm(EMPTY);
      setOpen(false);
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Nie udało się zmienić hasła.";
      if (err.response?.status === 400) setErrors({ current_password: msg });
      else toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, testid) => (
    <div>
      <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label}</label>
      <Input data-testid={testid} type="password" autoComplete={k === "current_password" ? "current-password" : "new-password"} value={form[k]} onChange={set(k)} aria-invalid={!!errors[k]} className={errors[k] ? "border-rose-500" : ""} />
      {errors[k] && <p data-testid={`${testid}-error`} className="text-xs text-rose-500 mt-1.5">{errors[k]}</p>}
    </div>
  );

  return (
    <>
      {!forced && (
        <button
          data-testid="admin-change-password-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"
        >
          <KeyRound size={13} /> <span className="hidden sm:inline">Zmień hasło</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={(o) => { if (forced) return; setOpen(o); if (!o) { setForm(EMPTY); setErrors({}); } }}>
        <DialogContent data-testid="change-password-dialog" className="max-w-md rounded-2xl" onInteractOutside={(e) => forced && e.preventDefault()} onEscapeKeyDown={(e) => forced && e.preventDefault()} hideClose={forced}>
          <DialogHeader className="text-left">
            <DialogTitle className="font-display font-bold text-xl">{forced ? "Ustaw nowe hasło administratora" : "Zmiana hasła administratora"}</DialogTitle>
            <DialogDescription className="text-sm">
              {forced && <span data-testid="change-password-forced-note" className="block mb-2 font-semibold text-foreground">Logujesz się domyślnym hasłem z konfiguracji. Ze względów bezpieczeństwa musisz je zmienić, zanim uzyskasz dostęp do panelu.</span>}
              Nowe hasło: co najmniej 10 znaków, litery i cyfry. Po zmianie hasło z konfiguracji serwera przestaje działać.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} noValidate className="space-y-4 mt-2">
            {field("current_password", "Aktualne hasło", "change-password-current")}
            {field("new_password", "Nowe hasło", "change-password-new")}
            {field("confirm", "Powtórz nowe hasło", "change-password-confirm")}
            <div className="flex justify-end gap-3 pt-2">
              {!forced && <button type="button" data-testid="change-password-cancel" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full text-sm font-semibold border border-border hover:bg-muted">Anuluj</button>}
              <button type="submit" data-testid="change-password-submit" disabled={saving} className="px-6 py-2.5 rounded-full text-sm font-semibold bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Zapisz hasło
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
